import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Button, FormikInput, Modal, Typography, FormikSelect } from 'components'
import { UserAddressFormFragment } from './UserAdressFormFragment'
import { getPredefinedInvitedUserContactInfo } from 'store/actions/connections'

const useStyles = makeStyles({
  modal: {
    width: 600,
    padding: 32,
  },
  title: {
    marginBottom: 8,
  },
  subTitle: {
    marginBottom: 24,
    color: '#666',
  },
  btnGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 24,
  },
  saveButton: {
    marginLeft: 24,
  },
  countryField: {
    marginBottom: 16,
  },
})

const validationSchema = Yup.object({
  street: Yup.string()
    .trim()
    .max(100, 'Please ensure this field has no more than 100 characters')
    .required('Please fill in the field'),
  suite: Yup.string()
    .trim()
    .max(50, 'Please ensure this field has no more than 50 characters'),
  city: Yup.string()
    .trim()
    .max(50, 'Please ensure this field has no more than 50 characters')
    .required('Please fill in the field'),
  state: Yup.string()
    .trim()
    .required('Please fill in the field'),
  zip: Yup.string()
    .trim()
    .required('Please fill in the field')
    .test(
      'zip-validation',
      function(value) {
        return this.parent.country === 124 ? 'Please enter a valid postal code' : 'Only digits available'
      },
      function(value) {
        if (!value) return false

        const country = this.parent.country
        if (country == 124) {
          return /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(value)
        } else {
          return /^\d{5}$/.test(value)
        }
      }
    ),
  phone: Yup.string()
    .test('phone-validation', 'Please enter a valid phone number', function(value) {
      if (!value) return false

      const country = this.parent.country
      const phoneValue = value.toString().replace(/([+ -])/g, '')

      if (country === 124) {
        // Canada - Canadian phone: +1 + 10 digits = 11 total
        return phoneValue.length === 11 && phoneValue.startsWith('1')
      } else {
        // USA - US phone: +1 + 10 digits = 11 total
        return phoneValue.length === 11 && phoneValue.startsWith('1')
      }
    })
    .required('Please fill in the field'),
  country: Yup.string()
    .trim()
    .required('Please select a country'),
})

export const ContactInfoPopup = ({ isOpen, onClose, onSaveContactInfo, proId }) => {
  const classes = useStyles()
  const states = useSelector(state => state.profileCreation.states)
  const provinces = useSelector(state => state.profileCreation.provinces)
  const countries = useSelector(state => state.profileCreation.countries)
  const currentUser = useSelector(state => state.auth.currentUser)
  const dispatch = useDispatch()
  const [initialValues, setInitialValues] = useState({
    phone: '',
    street: '',
    suite: '',
    city: '',
    state: '',
    zip: '',
    country: 840,
  })

  useEffect(() => {
    getPredefinedContactInfo()
  }, [])

  const getPredefinedContactInfo = async () => {
    try {
      const data = await dispatch(getPredefinedInvitedUserContactInfo(proId))
      setInitialValues({
        ...data,
        country: data.country || 840,
      })
    } catch (e) {
      console.error(e)
    }
  }

  // Get location options based on country selection
  const getLocationOptions = country => {
    if (country === 124) return provinces
    return states
  }

  return (
    <Modal open={isOpen} handleClose={onClose} maxWidth={false} dialogClasses={{ paper: classes.paper }}>
      <div className={classes.modal}>
        <Typography className={classes.title}>Accept invitation</Typography>
        <Typography className={classes.subTitle}>
          Please provide your contact info that will be shared with the lawyer
        </Typography>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={values => {
            onSaveContactInfo(values)
          }}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className={classes.countryField}>
                <FormikSelect
                  variant="outlined"
                  name="country"
                  label="Country"
                  fullWidth
                  type="text"
                  options={countries}
                  handleChange={event => {
                    setFieldValue('country', event.target.value)
                    setFieldValue('state', '')
                  }}
                />
              </div>
              <UserAddressFormFragment
                key={`address-fragment-${values.country || 'default'}`}
                states={getLocationOptions(values.country)}
                provinces={provinces}
                country={values.country}
              />
              <div className={classes.btnGroup}>
                <Button variant="text" onClick={onClose}>
                  cancel
                </Button>
                <Button type="submit" variant="primary" className={classes.saveButton} circularSize={22}>
                  Submit
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}
